import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Box,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Switch,
  Skeleton,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  DarkMode as DarkModeIcon,
  HelpOutline as HelpOutlineIcon,
  BugReport as BugReportIcon,
  NewReleases as NewReleasesIcon,
  Keyboard as KeyboardIcon,
} from '@mui/icons-material';
import NavColumn from './NavColumn';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { applyNavWorkspace } from '@/shared/layout/nav/workspace';
import { useNavWorkspaces } from './useNavWorkspaces';
import GlobalSearch from './GlobalSearch';
import RouteFallback from '@/shared/routing/RouteFallback';
import { prefetchRoute } from '@/shared/routing/routePrefetch';
import { useAuth } from '@/shared/context/AuthContext';
import { useTheme as useAppTheme } from '@/shared/context/ThemeContext';
import { AuthAvatar } from '@/shared/avatar';
import { toast } from '@/shared/feedback/toast';
import {
  PageTitleProvider,
  usePageTitleContext,
} from '@/shared/context/PageTitleContext';
import NotificationBell from '@/modules/notification/components/NotificationBell';
import { usePermission } from '@/shared/hooks/usePermission';
import { useClinicScope } from '@/shared/context/ClinicScopeContext';
import { layout, elevation, radius, surfaces } from '@/shared/theme/tokens';
import ClinicSwitcher from './ClinicSwitcher';
import {
  getPlatformMenuItems,
  getRailMenuItems,
  getVisibleRails,
  resolveRailId,
  withMenuIcons,
  type RailId,
} from '@/shared/layout/nav';
import { SpotlightHostProvider } from '@/shared/layout/spotlight';
import { CreateDialogHostProvider } from '@/shared/layout/create-host';
import {
  ShortcutsHostProvider,
  useKeyboardShortcuts,
  useShortcutsHost,
} from '@/shared/layout/shortcuts';
import { Button } from '@/shared/ui';
import CallSoftphoneHost from '@/modules/calls/CallSoftphoneHost';

const NAV_COLLAPSED_KEY = 'navColumnCollapsed';

const readNavCollapsed = (): boolean => {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const drawerWidth = layout.drawerWidth;

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  height: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
  paddingTop: `${theme.mixins.toolbar.minHeight}px`,
  minWidth: 0,
}));

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: '#fff',
  color: '#333',
  boxShadow: 'none',
}));

const LayoutInner: React.FC = () => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [navCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const { title, titleLoading } = usePageTitleContext();
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useAppTheme();
  const { hasPermission } = usePermission();
  const { clinics, selected, setSelected, canViewAllClinics } = useClinicScope();
  const navigate = useNavigate();
  const { openShortcutsHelp } = useShortcutsHost();
  useKeyboardShortcuts();

  const isSuperAdmin = user?.role === 'super_admin';
  const accessOpts = useMemo(
    () => ({ role: user?.role, hasPermission, entitlements: user?.entitlements }),
    [user?.role, user?.entitlements, hasPermission]
  );

  const visibleRails = useMemo(
    () => (isSuperAdmin ? [] : getVisibleRails(accessOpts)),
    [accessOpts, isSuperAdmin]
  );
  const navWorkspaces = useNavWorkspaces(!isSuperAdmin);
  const preference = navWorkspaces.query.data;
  const activeWorkspace = preference?.workspaces.find((entry) => entry.id === preference.activeId);
  const customWorkspace = activeWorkspace?.id === 'default' ? undefined : activeWorkspace;
  const catalogItems = useMemo(() => {
    const map = {} as Record<RailId, ReturnType<typeof getRailMenuItems>>;
    for (const rail of visibleRails) {
      map[rail.id] = getRailMenuItems(rail.id, accessOpts);
    }
    return map;
  }, [accessOpts, visibleRails]);
  const sidebarRails = useMemo(
    () => (isSuperAdmin ? visibleRails : applyNavWorkspace(visibleRails, catalogItems, customWorkspace)),
    [catalogItems, customWorkspace, isSuperAdmin, visibleRails],
  );

  const navWidth = navCollapsed ? layout.navCollapsedWidth : drawerWidth;

  const toggleNavCollapsed = () => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // Preference is optional; the column still toggles for this session.
      }
      return next;
    });
  };
  const platformItems = useMemo(
    () => (isSuperAdmin ? withMenuIcons(getPlatformMenuItems(accessOpts)) : []),
    [accessOpts, isSuperAdmin]
  );

  // Optimistic rail click must survive until navigate() commits pathname (RR startTransition).
  const pendingRailRef = useRef<RailId | null>(null);
  const stickyPathnameRef = useRef<string | null>(null);

  const [activeRailId, setActiveRailId] = useState<RailId | null>(() =>
    resolveRailId(location.pathname)
  );

  const handleSelectRail = useCallback(
    (railId: RailId) => {
      pendingRailRef.current = railId;
      stickyPathnameRef.current = location.pathname;
      setActiveRailId(railId);
    },
    [location.pathname]
  );

  useEffect(() => {
    if (isSuperAdmin) {
      pendingRailRef.current = null;
      stickyPathnameRef.current = null;
      setActiveRailId(null);
      return;
    }

    const resolved = resolveRailId(location.pathname);

    // Keep optimistic rail while URL is still the pre-navigation path.
    if (
      pendingRailRef.current != null &&
      stickyPathnameRef.current != null &&
      location.pathname === stickyPathnameRef.current &&
      resolved !== pendingRailRef.current
    ) {
      return;
    }

    pendingRailRef.current = null;
    stickyPathnameRef.current = null;

    if (resolved == null) {
      // Neutral path (e.g. /profile): keep last rail if still visible
      setActiveRailId((prev) => {
        if (prev && visibleRails.some((r) => r.id === prev)) return prev;
        return visibleRails[0]?.id ?? 'overview';
      });
      return;
    }
    const allowed = visibleRails.some((r) => r.id === resolved)
      ? resolved
      : visibleRails[0]?.id ?? 'overview';
    setActiveRailId(allowed);
  }, [isSuperAdmin, location.pathname, visibleRails]);

  const isMessagingInbox = location.pathname === '/messaging';
  const isInternalChatInbox = location.pathname === '/internal-chat';
  const isFullBleedInbox = isMessagingInbox || isInternalChatInbox;

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    prefetchRoute('/profile');
    prefetchRoute('/settings');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleClinicChange = (clinicId: string) => {
    try {
      localStorage.setItem('clinicScope', clinicId);
    } catch (err) {
      console.error(err);
    }
    setSelected(clinicId);
    setTimeout(() => {
      window.location.reload();
    }, 150);
  };

  const accountDisplayName = user?.name
    ? user.name.trim().split(/\s+/).pop() || user.name
    : '';
  const accountFullName = (user?.name || user?.username || '').trim();
  const accountSubtitle =
    user?.staff?.designation?.trim() ||
    (user?.role ? t(`roles.${user.role}`, { defaultValue: user.role }) : '');
  const accountGender = user?.gender || user?.staff?.gender;
  const genderFallbackSrc =
    accountGender === 'female'
      ? '/avatar/businesswoman.png'
      : accountGender === 'male'
        ? '/avatar/workerman.png'
        : undefined;
  const accountInitials = (() => {
    const full = accountFullName;
    if (!full) return '?';
    const parts = full.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  })();
  const accountAvatarSx = {
    width: 40,
    height: 40,
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0,
    ...(!user?.avatar && !genderFallbackSrc
      ? {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }
      : !user?.avatar
        ? { bgcolor: 'transparent' }
        : {}),
  } as const;

  return (
    <Box sx={{ display: 'flex', height: '100%', minHeight: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          ml: `${navWidth}px`,
          width: `calc(100% - ${navWidth}px)`,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
            }),
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: layout.appBarHeight,
            height: layout.appBarHeight,
            px: { xs: 2, sm: 3 },
            gap: 1,
          }}
        >
          {/* Left — page title */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="h5"
              noWrap
              component="div"
              sx={{ minWidth: 0, maxWidth: { xs: 140, sm: 260, md: 420 } }}
            >
              {titleLoading ? (
                <Skeleton variant="text" width={180} sx={{ display: 'inline-block' }} />
              ) : (
                title
              )}
            </Typography>
          </Box>

          {/* Center — global search */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1,
            }}
          >
            <GlobalSearch />
          </Box>

          {/* Right — clinic switcher, notifications, account */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 0.5,
            }}
          >
            {clinics.length > 0 && (
              <>
                <ClinicSwitcher
                  clinics={clinics}
                  selected={selected}
                  canViewAllClinics={canViewAllClinics}
                  onClinicChange={handleClinicChange}
                />
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ mx: 1, my: 1, borderColor: 'divider' }}
                />
              </>
            )}
            <NotificationBell />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1, my: 1, borderColor: 'divider' }}
            />
            {user && (
              <Button
                intent="ghost"
                onClick={handleMenu}
                aria-controls={anchorEl ? 'account-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={anchorEl ? 'true' : undefined}
                aria-label={t('accountSettings')}
                sx={{
                  color: 'text.primary',
                  px: 1,
                  py: 0.5,
                  gap: 1,
                  maxWidth: { xs: 160, sm: 240, md: 280 },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {user.name && (
                  <Typography
                    variant="body2"
                    noWrap
                    component="span"
                    sx={{
                      maxWidth: { xs: 96, sm: 160, md: 200 },
                      display: { xs: 'none', sm: 'inline' },
                      fontWeight: 500,
                    }}
                  >
                    {t('welcomeUser', { name: accountDisplayName })}
                  </Typography>
                )}
                <AuthAvatar
                  apiSrc={user.avatar}
                  fallbackSrc={genderFallbackSrc}
                  alt={user.name}
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 0,
                    ...(!user.avatar && !genderFallbackSrc
                      ? {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                        }
                      : !user.avatar
                        ? { bgcolor: 'transparent' }
                        : {}),
                  }}
                >
                  {accountInitials}
                </AuthAvatar>
              </Button>
            )}
          </Box>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleClose}
            slotProps={{
              paper: {
                elevation: elevation.menu,
                sx: {
                  overflow: 'visible',
                  mt: 1.25,
                  minWidth: 260,
                  borderRadius: radius.md,
                  border: 1,
                  borderColor: 'divider',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 18,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: 1,
                    borderTop: 1,
                    borderColor: 'divider',
                  },
                },
              },
              list: {
                dense: true,
                sx: { py: 0.5 },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                maxWidth: 280,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <AuthAvatar
                apiSrc={user?.avatar}
                fallbackSrc={genderFallbackSrc}
                alt={accountFullName}
                sx={accountAvatarSx}
              >
                {accountInitials}
              </AuthAvatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" noWrap fontWeight={600}>
                  {accountFullName || '—'}
                </Typography>
                {accountSubtitle ? (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {accountSubtitle}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Divider />

            {/* Account */}
            <MenuItem onClick={handleProfile} sx={{ py: 1, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('profile')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>
            <MenuItem onClick={handleSettings} sx={{ py: 1, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('settings')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>

            <Divider />

            {/* Preferences */}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                toggleTheme();
              }}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <DarkModeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('darkMode')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
              <Switch
                edge="end"
                size="small"
                checked={mode === 'dark'}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleTheme()}
                inputProps={{ 'aria-label': t('darkMode') }}
              />
            </MenuItem>

            <Divider />

            {/* Support & resources (placeholders) */}
            <MenuItem
              onClick={() => {
                handleClose();
                toast.info(t('featureComingSoon', { feature: t('helpCenter') }));
              }}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <HelpOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('helpCenter')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                toast.info(t('featureComingSoon', { feature: t('reportBug') }));
              }}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BugReportIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('reportBug')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                toast.info(t('featureComingSoon', { feature: t('releaseNotes') }));
              }}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <NewReleasesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('releaseNotes')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                openShortcutsHelp();
              }}
              sx={{ py: 1, px: 2 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <KeyboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('keyboardShortcuts')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>

            <Divider />

            <MenuItem onClick={handleLogout} sx={{ py: 1, px: 2 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('logout')}
                slotProps={{ primary: { variant: 'body2' } }}
              />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          width: navWidth,
          flexShrink: 0,
          transition: (theme) =>
            theme.transitions.create('width', {
              duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
            }),
        }}
        aria-hidden
      />
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: navWidth,
          zIndex: (theme) => theme.zIndex.drawer,
          boxSizing: 'border-box',
          bgcolor: 'rgb(248, 248, 248)',
          transition: (theme) =>
            theme.transitions.create('width', {
              duration: theme.transitions.duration.standard,
            easing: theme.transitions.easing.easeInOut,
            }),
        }}
      >
        <NavColumn
          rails={sidebarRails}
          belowBrand={
            isSuperAdmin ? null : (
              <WorkspaceSwitcher
                collapsed={navCollapsed}
                preference={preference}
                catalogRails={visibleRails}
                catalogItems={catalogItems}
                busy={navWorkspaces.save.isPending}
                onSave={(payload) => navWorkspaces.save.mutateAsync(payload)}
              />
            )
          }
          platformItems={platformItems}
          activeRailId={activeRailId}
          onSelectRail={handleSelectRail}
          access={accessOpts}
          workspace={customWorkspace}
          collapsed={navCollapsed}
          onToggleCollapsed={toggleNavCollapsed}
        />
      </Box>

      <Main>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: isFullBleedInbox ? 'hidden' : 'auto',
            overflowX: 'hidden',
            ...(mode === 'light'
              ? {
                  bgcolor: surfaces.appBackgroundColor,
                  backgroundImage: surfaces.appBackgroundImage,
                  backgroundAttachment: 'fixed',
                }
              : {}),
          }}
        >
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </Main>
      <CallSoftphoneHost />
    </Box>
  );
};

const Layout: React.FC = () => {
  return (
    <PageTitleProvider>
      <SpotlightHostProvider>
        <ShortcutsHostProvider>
          <CreateDialogHostProvider>
            <LayoutInner />
          </CreateDialogHostProvider>
        </ShortcutsHostProvider>
      </SpotlightHostProvider>
    </PageTitleProvider>
  );
};

export default Layout;
